/**
 * Per-line "Subscribe and save" control for the cart drawer and cart page.
 *
 * Ported from the legacy Superstore cart, but the cart round-trip goes through
 * this theme's own pipeline -- `fetchConfig`, `cart_change_url` with a `sections`
 * list, then `morphSection` -- rather than reloading the whole cart section. In
 * the drawer that means the panel updates in place instead of flashing.
 *
 * The one piece worth carrying over verbatim is the merge in `#switchPlan`: if
 * another line already holds this variant on the plan being switched to, Shopify
 * will happily create a second line rather than combining them, so the two are
 * merged by hand.
 */

import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/** Legacy analytics event name — kept so existing reporting keeps working. */
const SUB_TOGGLE_EVENT = 'cart_subscription_toggle';

/** Shopify returns `null` for a one-time line; normalise both to ''. */
const planId = (value) => (value == null || value === '' ? '' : String(value));

class CartSubscribeToggle extends HTMLElement {
  /*
   * Everything is delegated from the host rather than bound to the buttons.
   *
   * When a plan changes, the cart section is morphed: the <cart-subscribe-toggle>
   * element itself survives -- so connectedCallback does NOT run again -- while
   * its children are replaced wholesale. Anything bound to a button in
   * connectedCallback is therefore dead after the first switch, which is why the
   * control worked once and then stopped. The host is the only stable node, so
   * it owns the listeners and resolves the target per event.
   */
  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('scroll', this.#reposition, { capture: true, passive: true });
    window.addEventListener('resize', this.#reposition);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    this.#bound = false;
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('scroll', this.#reposition, { capture: true });
    window.removeEventListener('resize', this.#reposition);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  #bound = false;

  /** Read fresh every time -- morph rewrites these attributes. */
  get itemKey() {
    return this.dataset.itemKey ?? '';
  }

  get #panel() {
    return this.querySelector('.cart-subscribe-toggle__panel');
  }

  get #trigger() {
    return this.querySelector('.cart-subscribe-toggle__trigger');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('.cart-subscribe-toggle__cta')) {
      this.#switchPlan(this.dataset.defaultPlanId || this.dataset.firstPlanId || '');
      return;
    }

    const option = target.closest('.cart-subscribe-toggle__option');
    if (option) {
      this.#setOpen(false);
      this.#switchPlan(option.getAttribute('data-plan') ?? '');
      return;
    }

    if (target.closest('.cart-subscribe-toggle__trigger')) {
      event.stopPropagation();
      this.#setOpen(!this.#isOpen());
    }
  };

  /** Fallback light-dismiss; the popover path handles this itself. */
  #onDocumentClick = (event) => {
    if (this.#usePopover()) return;
    if (!this.contains(/** @type {Node} */ (event.target))) this.#setOpen(false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#isOpen()) {
      this.#setOpen(false);
      this.#trigger?.focus();
    }
  };

  #usePopover() {
    const panel = this.#panel;
    return !!panel && typeof panel.showPopover === 'function';
  }

  /*
   * Promotion happens on demand, not once at startup: the panel is one of the
   * nodes morph replaces, so a panel promoted at connect time is gone after the
   * first plan change.
   */
  #ensurePopover() {
    const panel = this.#panel;
    if (!(panel instanceof HTMLElement) || !this.#usePopover()) return;
    if (panel.popover === 'auto') return;

    panel.popover = 'auto';
    panel.removeAttribute('hidden');
    panel.addEventListener('toggle', (event) => {
      const open = /** @type {any} */ (event).newState === 'open';
      this.#trigger?.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-open', open);
      if (open) this.#position();
    });
  }

  #isOpen() {
    const panel = this.#panel;
    if (!panel) return false;
    return this.#usePopover() && panel.popover === 'auto'
      ? panel.matches(':popover-open')
      : !panel.hasAttribute('hidden');
  }

  /** @param {boolean} open */
  #setOpen(open) {
    const panel = this.#panel;
    const trigger = this.#trigger;
    if (!panel || !trigger) return;

    if (open) this.#ensurePopover();

    if (this.#usePopover() && panel.popover === 'auto') {
      if (open) {
        /*
         * Order matters. `toggle` fires asynchronously, so positioning only from
         * that handler leaves a frame where the panel is already in the top layer
         * but still at its default coordinates -- visible as a flash. It showed up
         * on the first open after a plan change because morph hands back a fresh
         * panel with no inline coordinates to reuse.
         *
         * So: place it from the anchor before it is ever shown, then refine
         * synchronously once it has real dimensions to flip and clamp against.
         */
        this.#anchorTo(panel);
        panel.showPopover();
        this.#position();
      } else {
        panel.hidePopover();
      }
      return;
    }

    panel.toggleAttribute('hidden', !open);
    trigger.setAttribute('aria-expanded', String(open));
    this.classList.toggle('is-open', open);
  }

  /**
   * First-paint placement, before the panel has been shown and therefore before
   * it has a measurable height. Good enough to avoid a wrong-position frame;
   * #position() refines it immediately afterwards.
   *
   * @param {HTMLElement} panel
   */
  #anchorTo(panel) {
    const trigger = this.#trigger;
    if (!trigger) return;
    const anchor = trigger.getBoundingClientRect();
    panel.style.minWidth = `${Math.max(anchor.width, 220)}px`;
    panel.style.top = `${anchor.bottom + 6}px`;
    panel.style.left = `${anchor.left}px`;
  }

  /**
   * Place the popover under its trigger, flipping above when the viewport runs
   * out below. Re-run on scroll and resize because the top layer does not move
   * with the drawer.
   */
  #position() {
    const panel = this.#panel;
    const trigger = this.#trigger;
    if (!(panel instanceof HTMLElement) || !trigger) return;

    const anchor = trigger.getBoundingClientRect();
    const gap = 6;
    panel.style.minWidth = `${Math.max(anchor.width, 220)}px`;

    const below = window.innerHeight - anchor.bottom - gap;
    const above = anchor.top - gap;
    const height = Math.min(panel.offsetHeight || 220, Math.max(below, above));
    const flip = below < height && above > below;

    panel.style.maxHeight = `${Math.max(120, height)}px`;
    panel.style.top = flip ? `${Math.max(gap, anchor.top - height - gap)}px` : `${anchor.bottom + gap}px`;

    const width = panel.offsetWidth || 220;
    const left = Math.min(Math.max(gap, anchor.left), window.innerWidth - width - gap);
    panel.style.left = `${left}px`;
  }

  #reposition = () => {
    if (this.#isOpen()) this.#position();
  };

  /** @param {boolean} on */
  #setBusy(on) {
    this.toggleAttribute('aria-busy', on);
    const row = this.closest('.cart-items__table-row');
    if (row instanceof HTMLElement) row.classList.toggle('cart-items__table-row--busy', on);
  }

  /** The cart-items components on the page, so every one of them re-renders. */
  #sectionIds() {
    const ids = new Set();
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    }
    return [...ids];
  }

  /**
   * Move this line onto `sellingPlan` ('' for a one-time purchase).
   * @param {string} sellingPlan
   */
  async #switchPlan(sellingPlan) {
    if (this.hasAttribute('aria-busy')) return;
    const from = planId(this.dataset.currentPlanId);
    const to = planId(sellingPlan);
    if (from === to) return;

    this.#setBusy(true);

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      const current = cart.items.find((/** @type {any} */ i) => i.key === this.itemKey);

      // The line vanished under us -- another tab, or an expired cart.
      if (!current) {
        window.location.reload();
        return;
      }

      /*
       * Shopify keys a cart line on variant + selling plan + properties, so
       * switching a line onto a plan another line already uses would leave two
       * lines for the same thing. Merge instead: add this quantity to the other
       * line and zero this one.
       */
      const twin = cart.items.find(
        (/** @type {any} */ i) =>
          i.key !== this.itemKey &&
          i.variant_id === current.variant_id &&
          planId(i.selling_plan_allocation?.selling_plan?.id) === to
      );

      const sectionIds = this.#sectionIds();
      const body = twin
        ? {
            updates: { [twin.key]: twin.quantity + current.quantity, [this.itemKey]: 0 },
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }
        : {
            id: this.itemKey,
            quantity: current.quantity,
            selling_plan: to,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          };

      /*
       * Channel discount flag, matching the legacy cart exactly: carried only
       * when moving ONTO a plan, and only for a line the server already marked.
       * Deliberately not widened to the visitor-level channel session -- this
       * property drives real money at checkout.
       */
      if (to && !twin && current.properties?._discount === 'Yes') {
        body.properties = { _discount: 'Yes' };
      }

      const url = twin ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;
      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
      const result = await response.json();

      if (result.errors) {
        console.error('[cart-subscribe-toggle]', result.errors);
        return;
      }

      this.#publish(from, to);

      for (const sectionId of sectionIds) {
        const html = result.sections?.[sectionId];
        if (!html) continue;
        morphSection(sectionId, html, { mode: 'hydration' });
      }

      document.dispatchEvent(
        new CartUpdateEvent(result, sectionIds[0] ?? '', {
          source: 'cart-subscribe-toggle',
          sections: result.sections,
        })
      );
    } catch (error) {
      console.error('[cart-subscribe-toggle]', error);
      window.location.reload();
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * @param {string} fromPlanId
   * @param {string} toPlanId
   */
  #publish(fromPlanId, toPlanId) {
    try {
      window.Shopify?.analytics?.publish?.(SUB_TOGGLE_EVENT, {
        source: 'cart',
        lineKey: this.itemKey,
        productId: this.dataset.productId || null,
        variantId: this.dataset.variantId || null,
        fromPlanId,
        toPlanId,
      });
    } catch {
      /* analytics must never break the cart */
    }
  }
}

if (!customElements.get('cart-subscribe-toggle')) {
  customElements.define('cart-subscribe-toggle', CartSubscribeToggle);
}
